import { createAttractionThemesConfig, type AttractionTheme } from '@/config/objects/attraction-themes';
import { AttractionThemeTranslationsList } from '@/components/attractions/themes/translations';
import { GenerateVoiceoversDialog } from '@/components/attractions/themes';
import { CrudDetailView, PageContainer, useApiClient } from '@startupquickstart/frontend';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AudioLines } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

/**
 * Attraction theme detail page
 */
export function AttractionThemeDetailPage() {
  const { attractionId, themeId } = useParams<{ attractionId: string; themeId: string }>();
  const navigate = useNavigate();
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const [currentTheme, setCurrentTheme] = useState<AttractionTheme | null>(null);
  const [voiceoversDialogOpen, setVoiceoversDialogOpen] = useState(false);

  const themesConfig = useMemo(
    () => (attractionId ? createAttractionThemesConfig(attractionId) : null),
    [attractionId],
  );

  /**
   * Delete theme mutation
   */
  const deleteMutation = useMutation({
    mutationFn: async (id: string): Promise<{ success: boolean; error?: string }> => {
      return apiClient.delete(`/attractions/${attractionId}/themes/${id}`);
    },
    onSuccess: (response: { success: boolean; error?: string }) => {
      if (response.success) {
        queryClient.invalidateQueries({ queryKey: ['AttractionTheme'] });
        navigate(`/attractions/${attractionId}`);
      } else {
        throw new Error(response.error || 'Failed to delete theme');
      }
    },
  });

  if (!attractionId || !themeId || !themesConfig) {
    return (
      <PageContainer>
        <div className="text-center">
          <h2 className="text-xl font-semibold mb-2">Theme Not Found</h2>
          <p className="text-muted-foreground">The requested theme could not be found.</p>
        </div>
      </PageContainer>
    );
  }

  /**
   * Navigate to edit form
   */
  const handleEdit = (theme: AttractionTheme) => {
    navigate(`/attractions/${attractionId}/themes/${theme.id}/edit`);
  };

  /**
   * Handle theme deletion
   */
  const handleDelete = async (theme: AttractionTheme) => {
    return toast.promise(deleteMutation.mutateAsync(theme.id), {
      loading: 'Deleting theme...',
      success: 'Theme deleted successfully',
      error: (error: Error) => error?.message || 'Failed to delete theme',
    });
  };

  const breadcrumbs = [
    { label: 'Home', href: '/' },
    { label: 'Attractions', href: '/attractions' },
    { label: currentTheme?.attraction?.name || 'Attraction', href: `/attractions/${attractionId}` },
    { label: 'Themes', href: `/attractions/${attractionId}` },
    { label: currentTheme?.name || currentTheme?.themeType?.name || 'Theme', current: true },
  ];

  return (
    <PageContainer>
      <div className="space-y-6">
        <CrudDetailView
          config={themesConfig}
          itemId={themeId}
          breadcrumbs={breadcrumbs}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onData={(themeData: AttractionTheme) => setCurrentTheme(themeData)}
          customActions={[
            {
              key: 'generate-voiceovers',
              label: 'Generate Voiceovers',
              icon: AudioLines,
              variant: 'secondary',
              onClick: () => setVoiceoversDialogOpen(true),
            },
          ]}
        />

        {/* Theme Translations Section */}
        {attractionId && themeId && (
          <AttractionThemeTranslationsList
            attractionId={attractionId}
            themeId={themeId}
            className="p-0"
          />
        )}

        {/* Generate Voiceovers Dialog */}
        {attractionId && themeId && (
          <GenerateVoiceoversDialog
            attractionId={attractionId}
            themeId={themeId}
            open={voiceoversDialogOpen}
            onClose={() => setVoiceoversDialogOpen(false)}
          />
        )}
      </div>
    </PageContainer>
  );
}
